import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Alert } from 'react-bootstrap';
import {
  Row,
  Col,
  Card,
  Button,
  Pagination,
  Form,
  Badge,
  Spinner,
  ProgressBar,
  Container
} from "react-bootstrap";
import { Modal } from "react-bootstrap";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { Pie } from "react-chartjs-2";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import "../../../../css/history.css";
import { CSVLink } from "react-csv";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useDispatch, useSelector } from "react-redux";
import { GetAllDietPlanByUser } from "../../../../core/store/diet/dietPan/thunk";
import DEJEUNER from "../../../../assets/images/diet/dejeuner1.png";
import MealCard from "./MealCard";

ChartJS.register(ArcElement, Tooltip, Legend);

const MealHistory = () => {
  // États locaux
  const [programs, setPrograms] = useState([]);
  const [filteredPrograms, setFilteredPrograms] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [dateRange, setDateRange] = useState([null, null]);
  const [expandedProgram, setExpandedProgram] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState('all'); // 'all', 'completed', 'inProgress'
  const [imageModal, setImageModal] = useState({ show: false, src: '', title: '' });
  const [videoModal, setVideoModal] = useState({ show: false, src: '', title: '' });
  const [media, setMedia] = useState({
    type: null, // can be 'image', 'video', or null
    src: '',
    title: ''
  });

   // État partagé : géré par le parent
   const [selectedMedia, setSelectedMedia] = useState(null);

   const openMedia = (type, src, title) => {
     setSelectedMedia({ type, src, title });
   };
 
   const closeMedia = () => {
     setSelectedMedia(null);
   };
  // Constants
  const itemsPerPage = 6;
  
  // Redux
  const dispatch = useDispatch();
  const { mealPlans = [], loading = false, error = null } = useSelector((state) => state.DietPlan || {});

  // Chargement initial des données
  useEffect(() => {
    const userId = JSON.parse(sessionStorage.getItem('authUser'))?.data?.userId;
    if (userId) {
      dispatch(GetAllDietPlanByUser(userId));
    }
  }, [dispatch]);

  // Fonction pour déterminer si un programme est passé
  const isPastProgram = useCallback((dailyDate) => {
    const today = moment().startOf('day');
    const programDate = moment(dailyDate).startOf('day');
    return programDate.isBefore(today);
  }, []);

  // Fonction pour calculer le pourcentage de repas consommés
  const calculateMealProgress = useCallback((dailyMeals = []) => {
    if (dailyMeals.length === 0) return 0;
    const consumedMeals = dailyMeals.filter(meal => meal.consumed).length;
    return Math.round((consumedMeals / dailyMeals.length) * 100);
  }, []);

  // Traitement des données des plans de repas
  useEffect(() => {
    if (mealPlans && mealPlans.length > 0) {
      const allDailyPlans = [];
      
      mealPlans.forEach((plan) => {
        if (plan.dailyPlans && Array.isArray(plan.dailyPlans)) {
          plan.dailyPlans.forEach((dailyPlan) => {
            if (!dailyPlan.dailyDate) return;
            
            try {
              const isCompleted = isPastProgram(dailyPlan.dailyDate);
              const mealProgress = calculateMealProgress(dailyPlan.dailyMeals);
              
              allDailyPlans.push({
                ...dailyPlan,
                goal: plan.goal || {},
                originalStatus: plan.status || 'UNKNOWN',
                computedStatus: isCompleted ? 'COMPLETED' : 'IN_PROGRESS',
                isPast: isCompleted,
                mealProgress: mealProgress,
                consumedMealsCount: dailyPlan.dailyMeals?.filter(meal => meal.consumed).length || 0,
                totalMealsCount: dailyPlan.dailyMeals?.length || 0
              });
            } catch (e) {
              console.error('Error processing daily plan:', e);
            }
          });
        }
      });
      
      // Tri par date décroissante
      allDailyPlans.sort((a, b) => {
        try {
          return moment(b.dailyDate).diff(moment(a.dailyDate));
        } catch (e) {
          return 0;
        }
      });
      
      setPrograms(allDailyPlans);
      setFilteredPrograms(allDailyPlans);
    }
  }, [mealPlans, isPastProgram, calculateMealProgress]);

    
  const openImageModal = (imageSrc, title) => {
    setImageModal({ show: true, src: imageSrc, title });
  };

  const closeImageModal = () => {
    setImageModal({ show: false, src: '', title: '' });
  };
      
  // Gestion du filtre par mode de vue
  const handleViewModeChange = useCallback((mode) => {
    setViewMode(mode);
    let filtered = programs;
    
    switch (mode) {
      case 'completed':
        filtered = programs.filter(program => program.isPast);
        break;
      case 'inProgress':
        filtered = programs.filter(program => !program.isPast);
        break;
      default:
        filtered = programs;
    }
    
    // Appliquer également les autres filtres
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(program =>
        program.dailyDate.includes(term) ||
        program.dailyMeals?.some(meal => 
          meal.recipe?.recipeName?.toLowerCase().includes(term) ||
          meal.recipe?.category?.toLowerCase().includes(term)
        )
      );
    }
    
    setFilteredPrograms(filtered);
    setCurrentPage(1);
  }, [programs, searchTerm]);

  // Gestion du filtre par date
  const handleDateFilter = useCallback(() => {
    const [startDate, endDate] = dateRange;
    if (startDate && endDate) {
      const filtered = programs.filter((program) => {
        const programDate = moment(program.dailyDate);
        return programDate.isBetween(moment(startDate), moment(endDate), null, "[]");
      });
      setFilteredPrograms(filtered);
    } else {
      handleViewModeChange(viewMode);
    }
    setCurrentPage(1);
  }, [dateRange, programs, viewMode, handleViewModeChange]);

  // Gestion de la recherche
  const handleSearch = useCallback((e) => {
    const term = e.target.value.toLowerCase();
    setSearchTerm(term);
    
    let filtered = programs;
    
    // Filtre par mode de vue
    switch (viewMode) {
      case 'completed':
        filtered = programs.filter(program => program.isPast);
        break;
      case 'inProgress':
        filtered = programs.filter(program => !program.isPast);
        break;
      default:
        filtered = programs;
    }
    
    // Filtre par recherche
    if (term) {
      filtered = filtered.filter(program =>
        program.dailyDate.includes(term) ||
        program.dailyMeals?.some(meal => 
          meal.recipe?.recipeName?.toLowerCase().includes(term) ||
          meal.recipe?.category?.toLowerCase().includes(term)
        )
      );
    }
    
    setFilteredPrograms(filtered);
    setCurrentPage(1);
  }, [programs, viewMode]);

  const openVideoModal = (videoSrc, title) => {
    setVideoModal({ show: true, src: videoSrc, title });
  };
  
  const closeVideoModal = () => {
    setVideoModal({ show: false, src: '', title: '' });
  };
  // Toggle pour développer/réduire un programme
  const toggleProgram = useCallback((programId) => {
    setExpandedProgram(prev => prev === programId ? null : programId);
  }, []);

  // Pagination
  const { currentItems, totalPages } = useMemo(() => {
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const items = filteredPrograms.slice(indexOfFirstItem, indexOfLastItem);
    const pages = Math.ceil(filteredPrograms.length / itemsPerPage);
    
    return { currentItems: items, totalPages: pages };
  }, [filteredPrograms, currentPage]);

  // Statistiques globales
  const globalStats = useMemo(() => {
    const totalPrograms = programs.length;
    const completedPrograms = programs.filter(p => p.isPast).length;
    const inProgressPrograms = programs.filter(p => !p.isPast).length;
    const totalCalories = programs.reduce((sum, program) => sum + (program.totalCalories || 0), 0);
    const averageProgress = programs.length > 0 
      ? Math.round(programs.reduce((sum, program) => sum + program.mealProgress, 0) / programs.length)
      : 0;
    
    return { 
      totalPrograms, 
      completedPrograms, 
      inProgressPrograms, 
      totalCalories, 
      averageProgress,
      filteredCount: filteredPrograms.length
    };
  }, [programs, filteredPrograms.length]);

  // Fonction pour obtenir la variante de couleur selon le statut
  const getStatusVariant = (program) => {
    if (program.isPast) {
      return program.mealProgress === 100 ? 'success' : 'warning';
    }
    return 'info';
  };

  // Fonction pour obtenir le texte du statut
  const getStatusText = (program) => {
    if (program.isPast) {
      if (program.mealProgress === 100) return 'Terminé avec succès';
      if (program.mealProgress > 0) return `Partiellement terminé (${program.mealProgress}%)`;
      return 'Non suivi';
    }
    return 'En cours';
  };

  // Composant pour les détails des repas
  const MealDetails = ({ meals, isPast }) => {
    if (!meals || meals.length === 0) {
      return (
        <div className="text-center py-4">
          <div className="text-muted mb-2">Aucun repas planifié</div>
          <small className="text-muted">Ce jour ne contient aucun repas dans le plan</small>
        </div>
      );
    }


    return (
      <div className="meals-container">
          {meals.map((meal, index) => (
              <MealCard key={index} meal={meal} isPast={isPast} />
          ))}
      </div>
  ); }

  // Composant pour les objectifs quotidiens et activités
  const DailyGoalsAndActivities = ({ dailyPlan }) => (
    <div className="goals-activities">
      <Card className="mb-3">
        <Card.Header>
          <h6 className="mb-0">Objectifs nutritionnels</h6>
        </Card.Header>
        <Card.Body>
          <div className="d-flex flex-wrap gap-2 mb-3">
            <Badge bg="secondary">Calories: {dailyPlan.totalCalories || 0} kcal</Badge>
            {dailyPlan.dailyGoal && (
              <>
                <Badge bg="info" text="dark">Protéines: {dailyPlan.dailyGoal.protein || 0}g</Badge>
                <Badge bg="warning" text="dark">Lipides: {dailyPlan.dailyGoal.fats || 0}g</Badge>
                <Badge bg="success">Glucides: {dailyPlan.dailyGoal.carbs || 0}g</Badge>
              </>
            )}
          </div>
          
          {dailyPlan.dailyGoal?.repartition?.length > 0 && (
            <div>
              <small className="text-muted d-block mb-2">Répartition des repas:</small>
              {dailyPlan.dailyGoal.repartition.map((rep, index) => (
                <div key={index} className="mb-2 p-2 bg-light rounded">
                  <div className="d-flex justify-content-between align-items-center">
                    <small className="fw-semibold">Repas {index + 1}</small>
                    <div className="d-flex gap-1 flex-wrap">
                      <Badge bg="outline-primary" text="dark" style={{ fontSize: '0.7rem' }}>{rep.calories} kcal</Badge>
                      <Badge bg="outline-info" text="dark" style={{ fontSize: '0.7rem' }}>P: {rep.protein}g</Badge>
                      <Badge bg="outline-warning" text="dark" style={{ fontSize: '0.7rem' }}>L: {rep.fats}g</Badge>
                      <Badge bg="outline-success" text="dark" style={{ fontSize: '0.7rem' }}>G: {rep.carbs}g</Badge>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card.Body>
      </Card>
      
      {dailyPlan.activities?.length > 0 && (
        <Card>
          <Card.Header>
            <h6 className="mb-0">Activités physiques</h6>
          </Card.Header>
          <Card.Body>
            {dailyPlan.activities.map((activity, idx) => (
              <div key={idx} className="d-flex justify-content-between align-items-center py-2 border-bottom border-light">
                <div>
                  <div className="fw-semibold">{activity.activity.name}</div>
                  <small className="text-muted">{activity.duration} minutes</small>
                </div>
                <Badge bg="outline-primary" text="dark">MET: {activity.activity.met}</Badge>
              </div>
            ))}
          </Card.Body>
        </Card>
      )}
    </div>
  );

  // Composant pour le graphique des macronutriments
  const MacroChart = ({ dailyGoal }) => {
    if (!dailyGoal?.protein || !dailyGoal?.fats || !dailyGoal?.carbs) {
      return (
        <div className="text-center py-4">
          <div className="text-muted">Données nutritionnelles indisponibles</div>
        </div>
      );
    }

    return (
      <Card>
        <Card.Header>
          <h6 className="mb-0">Répartition des macronutriments</h6>
        </Card.Header>
        <Card.Body>
          <div style={{ height: '250px' }}>
            <Pie
              data={{
                labels: ["Protéines", "Lipides", "Glucides"],
                datasets: [{
                  data: [dailyGoal.protein, dailyGoal.fats, dailyGoal.carbs],
                  backgroundColor: ["#0d6efd", "#ffc107", "#198754"],
                  borderColor: "#fff",
                  borderWidth: 2,
                }],
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: "bottom",
                    labels: {
                      color: "#343a40",
                      font: { size: 11 },
                      padding: 15,
                    },
                  },
                },
              }}
            />
          </div>
        </Card.Body>
      </Card>
    );
  };

  // Contenu conditionnel basé sur l'état
  const getMainContent = () => {
    if (loading) {
      return (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Chargement de vos programmes alimentaires...</p>
        </div>
      );
    }
    
    if (error) {
      return (
        <Alert variant="danger" className="text-center">
          <Alert.Heading>Erreur de chargement</Alert.Heading>
          <p>Une erreur s'est produite lors du chargement des plans: {error}</p>
          <Button variant="outline-danger" onClick={() => window.location.reload()}>
            Réessayer
          </Button>
        </Alert>
      );
    }
    
    if (filteredPrograms.length === 0) {
      return (
        <div className="text-center py-5">
          <div className="mb-4" style={{ fontSize: '4rem', opacity: 0.3 }}>📅</div>
          <h5 className="text-muted mb-3">Aucun programme trouvé</h5>
          <p className="text-muted">
            {programs.length === 0 
              ? "Vous n'avez pas encore de programmes alimentaires"
              : "Aucun programme ne correspond à vos critères de recherche"
            }
          </p>
          {programs.length > 0 && (
            <Button variant="outline-primary" onClick={() => {
              setSearchTerm("");
              setDateRange([null, null]);
              setViewMode('all');
              setFilteredPrograms(programs);
            }}>
              Réinitialiser les filtres
            </Button>
          )}
        </div>
      );
    }

    return (
      <div className="programs-grid">
        {currentItems.map((program) => (
          <Card key={program.id} className={`program-card mb-4 shadow-sm ${program.isPast ? 'past-program' : 'current-program'}`}>
            <Card.Header
              onClick={() => toggleProgram(program.id)}
              style={{ cursor: "pointer" }}
              className="user-select-none bg-light"
            >
              <div className="d-flex justify-content-between align-items-center">
                <div className="flex-grow-1">
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <h5 className="mb-0">
                      {moment(program.dailyDate).format('dddd DD MMMM YYYY')}
                    </h5>
                    {program.isPast && (
                      <Badge bg="secondary" className="small">Passé</Badge>
                    )}
                  </div>
                  
                  <div className="d-flex gap-2 flex-wrap mb-2">
                    <Badge bg={getStatusVariant(program)}>
                      {getStatusText(program)}
                    </Badge>
                    <Badge bg="info">
                      {program.totalCalories || 0} kcal
                    </Badge>
                    <Badge bg="secondary">
                      {program.consumedMealsCount}/{program.totalMealsCount} repas
                    </Badge>
                  </div>
                  
                  {program.totalMealsCount > 0 && (
                    <div className="progress-container">
                      <small className="text-muted d-block mb-1">
                        Progression des repas: {program.mealProgress}%
                      </small>
                      <ProgressBar 
                        now={program.mealProgress} 
                        variant={program.mealProgress === 100 ? 'success' : 'info'}
                        style={{ height: '6px' }}
                      />
                    </div>
                  )}
                </div>
                <div className="expand-icon">
                  <span className="fs-4">{expandedProgram === program.id ? "−" : "+"}</span>
                </div>
              </div>
            </Card.Header>
            
            {expandedProgram === program.id && (
              <Card.Body className="p-0">
                <Row className="g-0">
                  <Col lg={8} className="border-end">
                    <div className="p-3">
                      <h6 className="mb-3 text-primary">Détails des repas</h6>
                      <MealDetails meals={program.dailyMeals || []} isPast={program.isPast} />
                    </div>
                  </Col>
                  <Col lg={4}>
                    <div className="p-3">
                      <MacroChart dailyGoal={program.dailyGoal} />
                      <div className="mt-3">
                        <DailyGoalsAndActivities dailyPlan={program} />
                      </div>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            )}
          </Card>
        ))}
      </div>
    );
  };

  // Rendu principal avec un seul return
  return (
    <Container fluid className="meal-history-container">
      <ToastContainer />
      
      {/* En-tête */}
      <div style={{marginTop:'-3rem'}} className="text-center mb-1">
        <h2 className="text-primary mb-2">Historique des Programmes Alimentaires</h2>
       
      </div>

      {/* Statistiques globales */}
      <Row >
        <Col>
          <Card className="shadow-sm border-0 bg-gradient">
            <Card.Body>
              <Row className="text-center">
                <Col md={3}>
                  <div className="stat-item">
                    <div className="stat-number text-primary fs-2 fw-bold">{globalStats.totalPrograms}</div>
                    <small className="text-muted">Total programmes</small>
                  </div>
                </Col>
                <Col md={3}>
                  <div className="stat-item">
                    <div className="stat-number text-success fs-2 fw-bold">{globalStats.completedPrograms}</div>
                    <small className="text-muted">Programmes passés</small>
                  </div>
                </Col>
                <Col md={3}>
                  <div className="stat-item">
                    <div className="stat-number text-info fs-2 fw-bold">{globalStats.inProgressPrograms}</div>
                    <small className="text-muted">En cours</small>
                  </div>
                </Col>
                <Col md={3}>
                  <div className="stat-item">
                    <div className="stat-number text-warning fs-2 fw-bold">{globalStats.averageProgress}%</div>
                    <small className="text-muted">Progression moyenne</small>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Filtres et actions */}
      <Card >
        <Card.Body>
          <Row className="align-items-end">
            <Col md={3}>
              <Form.Label className="small fw-semibold">Mode d'affichage</Form.Label>
              <Form.Select 
                value={viewMode} 
                onChange={(e) => handleViewModeChange(e.target.value)}
                size="sm"
              >
                <option value="all">Tous les programmes</option>
                <option value="completed">Programmes passés</option>
                <option value="inProgress">Programmes en cours</option>
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Label className="small fw-semibold">Rechercher</Form.Label>
              <Form.Control
                type="text"
                placeholder="Nom de recette, catégorie..."
                value={searchTerm}
                onChange={handleSearch}
                size="sm"
              />
            </Col>
            <Col md={3}>
              <Form.Label className="small fw-semibold">Période</Form.Label>
              <DatePicker
                selectsRange
                startDate={dateRange[0]}
                endDate={dateRange[1]}
                onChange={setDateRange}
                placeholderText="Sélectionner une période"
                className="form-control form-control-sm"
                dateFormat="dd/MM/yyyy"
              />
            </Col>
            <Col md={3}>
              <div className="d-flex gap-2">
                <Button 
                  variant="primary" 
                  size="sm"
                  onClick={handleDateFilter} 
                  disabled={!dateRange[0] || !dateRange[1]}
                >
                  Filtrer
                </Button>
                <CSVLink 
                  data={filteredPrograms} 
                  filename={`programmes_${moment().format('YYYY-MM-DD')}.csv`}
                  className="btn btn-success btn-sm text-decoration-none"
                >
                  Export CSV
                </CSVLink>
              </div>
            </Col>
          </Row>
          
          {globalStats.filteredCount < globalStats.totalPrograms && (
            <div className="mt-3">
              <Alert variant="info" className="mb-0 py-2">
                <small>
                  Affichage de {globalStats.filteredCount} programme{globalStats.filteredCount > 1 ? 's' : ''} 
                  sur {globalStats.totalPrograms} au total
                </small>
              </Alert>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Contenu principal */}
    
    <Row className="mt-2">
      <Col>
        {/* Contenu principal avec design amélioré */}
        <Card className="border-0 shadow-lg overflow-hidden">
          <Card.Body className="p-0">
            <div className="bg-gradient-to-r from-primary to-info text-white p-3 mb-0">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <h5 className="mb-1 fw-bold">Programmes Alimentaires</h5>
                  <small className="opacity-75">
                    {globalStats.filteredCount} résultat{globalStats.filteredCount > 1 ? 's' : ''}
                    {viewMode === 'completed' && ' - Programmes terminés'}
                    {viewMode === 'inProgress' && ' - En cours'}
                  </small>
                </div>
                <div className="d-flex align-items-center gap-2">
                  {searchTerm && (
                    <Badge bg="light" text="dark" className="px-2 py-1">
                      <i className="bi bi-search me-1"></i>
                      "{searchTerm}"
                    </Badge>
                  )}
                  {dateRange[0] && dateRange[1] && (
                    <Badge bg="light" text="dark" className="px-2 py-1">
                      <i className="bi bi-calendar-range me-1"></i>
                      Période filtrée
                    </Badge>
                  )}
                </div>
              </div>
            </div>
            
            <div className="p-4">
              <div className="position-relative">
                {/* Indicateur de chargement si nécessaire */}
                {loading && (
                  <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-white bg-opacity-75" style={{zIndex: 10}}>
                    <div className="text-center">
                      <Spinner animation="border" variant="primary" />
                      <div className="mt-2 text-muted">Chargement...</div>
                    </div>
                  </div>
                )}
                
                {/* Animation de transition pour le contenu */}
                <div className={`transition-all duration-300 ${loading ? 'opacity-50' : 'opacity-100'}`}>
                  {getMainContent()}
                </div>
                
                {/* Message si aucun résultat */}
                {filteredPrograms.length === 0 && !loading && (
                  <div className="text-center py-5">
                    <div className="mb-3">
                      <i className="bi bi-search text-muted" style={{fontSize: '3rem'}}></i>
                    </div>
                    <h5 className="text-muted mb-2">Aucun programme trouvé</h5>
                    <p className="text-muted mb-3">
                      {searchTerm || dateRange[0] || viewMode !== 'all' 
                        ? 'Essayez de modifier vos critères de recherche' 
                        : 'Commencez par créer votre premier programme alimentaire'
                      }
                    </p>
                    {(searchTerm || dateRange[0] || viewMode !== 'all') && (
                      <Button 
                        variant="outline-primary" 
                        size="sm"
                        onClick={() => {
                          setSearchTerm('');
                          setDateRange([null, null]);
                          setViewMode('all');
                        }}
                      >
                        <i className="bi bi-arrow-clockwise me-1"></i>
                        Réinitialiser les filtres
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </Card.Body>
        </Card>

        {/* Pagination améliorée */}
        {totalPages > 1 && (
          <div className="d-flex justify-content-center mt-4">
            <Card className="border-0 shadow-sm">
              <Card.Body className="p-2">
                <div className="d-flex align-items-center gap-3">
                  {/* Info pagination */}
                  <small className="text-muted d-none d-md-block">
                    Page {currentPage} sur {totalPages}
                  </small>
                  
                  {/* Contrôles pagination */}
                  <Pagination className="mb-0 shadow-sm">
                    <Pagination.First
                      onClick={() => setCurrentPage(1)}
                      disabled={currentPage === 1}
                      className="d-none d-md-block"
                    />
                    <Pagination.Prev
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                    />
                    
                    {/* Pages dynamiques */}
                    {[...Array(Math.min(totalPages, 5))].map((_, index) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = index + 1;
                      } else {
                        if (currentPage <= 3) {
                          pageNum = index + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + index;
                        } else {
                          pageNum = currentPage - 2 + index;
                        }
                      }

                      return (
                        <Pagination.Item
                          key={pageNum}
                          active={pageNum === currentPage}
                          onClick={() => setCurrentPage(pageNum)}
                          className="px-1"
                        >
                          {pageNum}
                        </Pagination.Item>
                      );
                    })}
                    
                    <Pagination.Next
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                      disabled={currentPage === totalPages}
                    />
                    <Pagination.Last
                      onClick={() => setCurrentPage(totalPages)}
                      disabled={currentPage === totalPages}
                      className="d-none d-md-block"
                    />
                  </Pagination>
                  
                  {/* Saut rapide */}
                  {totalPages > 10 && (
                    <div className="d-none d-lg-flex align-items-center gap-2">
                      <small className="text-muted">Aller à :</small>
                      <Form.Control
                        type="number"
                        size="sm"
                        min="1"
                        max={totalPages}
                        value={currentPage}
                        onChange={(e) => {
                          const page = parseInt(e.target.value);
                          if (page >= 1 && page <= totalPages) {
                            setCurrentPage(page);
                          }
                        }}
                        style={{width: '70px'}}
                        className="text-center"
                      />
                    </div>
                  )}
                </div>
              </Card.Body>
            </Card>
          </div>
        )}
      </Col>
    </Row>

 
  {/* Modal pour l'affichage des images */}
  <Modal show={imageModal.show} onHide={closeImageModal}  centered>
      <Modal.Header closeButton>
        <Modal.Title>{imageModal.title}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-0">
        <img 
          src={imageModal.src} 
          alt={imageModal.title}
          className="w-100 "
          style={{ maxHeight: '50vh', objectFit: 'contain' }}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={closeImageModal}>
          Fermer
        </Button>
      </Modal.Footer>
    </Modal>

     {/* Modal pour l'affichage des vidéos */}
    <Modal show={videoModal.show} onHide={closeVideoModal}  centered>
      <Modal.Header closeButton>
        <Modal.Title>{videoModal.title}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-0">
        <video 
          src={videoModal.src}
          controls
          className="w-100 h-100"
          style={{minWidth: '30vh', minHeight: '40vh', objectFit: 'contain' }}
        >
          Votre navigateur ne supporte pas la lecture de vidéos.
        </video>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={closeVideoModal}>
          Fermer
        </Button>
      </Modal.Footer>
    </Modal>
    </Container>
  );
};

export default MealHistory;